import { useEffect, useMemo, useRef, useState } from "react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";

/**
 * Searchable category combobox.
 *  - Clicking the trigger ALWAYS opens (no need to clear current value).
 *  - Built-in search filter.
 *  - Footer button: "+ Create New Category" → opens inline dialog → on save
 *    auto-selects the new category and closes the popover.
 *
 * Props:
 *   value:     selected category name (string)
 *   onChange:  (name) => void
 *   categories: TagCategory[] from /api/tag-categories
 *   onCategoriesChange: () => void — invoked after a new category is created
 *   excludeInactive: boolean (default true) — hide is_active=false categories
 *   triggerTestId: string for the trigger button data-testid
 */
export default function CategoryCombobox({
  value,
  onChange,
  categories,
  onCategoriesChange,
  excludeInactive = true,
  placeholder = "Select category",
  triggerTestId = "category-combobox-trigger",
}) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const visible = useMemo(
    () => (excludeInactive ? categories.filter((c) => c.is_active !== false) : categories),
    [categories, excludeInactive]
  );

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Category name required");
    setSaving(true);
    try {
      const { data } = await api.post("/tag-categories", { name, is_active: true });
      toast.success(`Category "${data.name}" created`);
      onCategoriesChange?.();
      onChange(data.name);
      setCreateOpen(false);
      setNewName("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid={triggerTestId}
            className="w-full justify-between font-normal"
          >
            <span className={value ? "text-slate-900" : "text-slate-400"}>
              {value || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
          <Command>
            <CommandInput
              placeholder="Search category…"
              data-testid="category-combobox-search"
              ref={inputRef}
            />
            <CommandList>
              <CommandEmpty>No matching category.</CommandEmpty>
              <CommandGroup>
                {visible.map((c) => (
                  <CommandItem
                    key={c.id || c.name}
                    value={c.name}
                    onSelect={() => {
                      onChange(c.name);
                      setOpen(false);
                    }}
                    data-testid={`category-option-${c.name}`}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        value === c.name ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span className="flex-1 truncate">{c.name}</span>
                    {typeof c.tag_count === "number" && (
                      <span className="text-[10px] text-slate-400 ml-2">{c.tag_count}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => setCreateOpen(true)}
                  data-testid="category-combobox-create"
                  className="text-brand-dark"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create new category
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New category</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">
              Category name
            </Label>
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Campaign Source"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !saving) handleCreate();
              }}
              data-testid="category-create-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} data-testid="category-create-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-brand hover:bg-brand-dark"
              data-testid="category-create-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Hook to load + refresh categories from the new endpoint.
export function useTagCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tag-categories");
      setCategories(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  return { categories, loading, reload };
}
